import { useEffect, useState } from "react";

import { ActionBar } from "../../components/ActionBar";
import { Field, FieldGrid, FieldSet } from "../../components/Fields";
import { I } from "../../components/icons";
import { Notice } from "../../components/Notice";
import { NavRow, RowGroup } from "../../components/Rows";
import { Button } from "../../components/Button";
import { Sheet } from "../../components/Sheet";
import { todayStr } from "../../domain/date";
import { foodOn } from "../../domain/intake";
import { remindersSummary } from "../../domain/water";
import { notificationPermission } from "../../lib/device";
import { downloadIcs, icsCalendar } from "../../lib/ics";
import { pushHere, type PushHere } from "../../lib/push";
import { useKaya } from "../../store/kayaStore";
import { useProfileForm } from "./profileForm";
import SettingsPage from "./SettingsPage";

/** A framed page can never show a permission prompt, so say so rather than fail silently. */
function inIframe(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

export default function AlertSettings() {
  const { f, set, save, dirty } = useProfileForm();
  const showToast = useKaya((s) => s.showToast);
  const S = useKaya((s) => s.S);
  const [askingAt, setAskingAt] = useState(false);
  const food = foodOn(S);
  /** Whether this device receives pushes: the dinner notice says where the nudge arrives from. */
  const [here, setHere] = useState<PushHere | null>(null);
  useEffect(() => {
    let live = true;
    void pushHere().then((h) => {
      if (live) setHere(h);
    });
    return () => {
      live = false;
    };
  }, []);

  const nperm = notificationPermission();
  const nvalue =
    nperm === "granted"
      ? "Allowed"
      : nperm === "denied"
        ? "Blocked"
        : nperm === "default"
          ? "Not asked"
          : "Not supported";
  const nstat =
    (nperm === "granted"
      ? "The browser allows notifications."
      : nperm === "denied"
        ? "Allow notifications for this site in the browser's site settings, or install the app and enable it there."
        : nperm === "default"
          ? "Saving with notifications on will ask."
          : "This browser cannot show notifications.") +
    (inIframe()
      ? " This preview runs inside another page, which blocks the prompt — it works once Kaya is installed."
      : "");

  function exportCalendar() {
    try {
      downloadIcs(
        "kaya-reminders.ics",
        icsCalendar([
          { time: f.remindMorning, title: "Kaya: morning session" },
          { time: f.remindEvening, title: "Kaya: treadmill" },
        ])
      );
    } catch {
      showToast("Could not build the calendar file");
    }
  }

  return (
    <SettingsPage
      title="Alerts & reminders"
      intro="Beeps during a session, notifications, and a daily nudge."
      onSave={() => void save()}
      dirty={dirty}
    >
      <RowGroup heading="During a session">
        <NavRow
          kind="toggle"
          title="Beep at changes"
          subtitle="The phone must be off silent."
          checked={f.sound}
          onChange={(v) => set("sound", v)}
        />
        <NavRow
          kind="toggle"
          title="Vibrate"
          subtitle="Android only."
          checked={f.vibrate}
          onChange={(v) => set("vibrate", v)}
        />
      </RowGroup>

      <RowGroup heading="Notifications">
        <NavRow
          kind="toggle"
          title="Allow notifications"
          subtitle="Rest over, treadmill speed changes and daily reminders — only while Kaya is not on screen."
          checked={f.notifyWanted}
          onChange={(v) => set("notifyWanted", v)}
        />
        <NavRow kind="value" icon={I.bell} title="Permission" subtitle={nstat} value={nvalue} />
      </RowGroup>

      <RowGroup heading="Daily reminders">
        <NavRow
          kind="toggle"
          title="Remind me"
          subtitle="At the times below on training days, Sunday excluded."
          checked={f.remindOn}
          onChange={(v) => set("remindOn", v)}
        />
      </RowGroup>

      <FieldSet
        legend="Reminder times"
        hint="A web app cannot run alarms in the background: a reminder fires only if Kaya was opened in the previous day."
      >
        <FieldGrid>
          <Field
            label="Morning session"
            type="time"
            value={f.remindMorning}
            onChange={(v) => set("remindMorning", v)}
          />
          <Field
            label="Evening treadmill"
            type="time"
            value={f.remindEvening}
            onChange={(v) => set("remindEvening", v)}
          />
        </FieldGrid>
      </FieldSet>

      {/* Board YouRemindersDinner. Only while food is tracked: with it off there is no dinner to
          ask about, and water is part of Food too. */}
      {food ? (
        <div className="you-stack">
          <RowGroup heading="Food and water">
            <NavRow
              kind="toggle"
              icon={I.bowl}
              title="Log dinner?"
              subtitle="A nudge if no dinner is logged by then"
              checked={f.dinnerRemindOn}
              onChange={(v) => set("dinnerRemindOn", v)}
            />
            {f.dinnerRemindOn ? (
              <NavRow title="Asks at" value={f.dinnerRemindAt} onClick={() => setAskingAt(true)} />
            ) : null}
            <NavRow
              icon={I.glass}
              title="Water"
              subtitle={remindersSummary(S, todayStr())}
              to="/you/reminders/water"
            />
          </RowGroup>
          <p className="you-fine">
            Log dinner? stays quiet once dinner is logged and while food tracking is off. It asks
            about the meal; it never counts what&rsquo;s missing.
          </p>
        </div>
      ) : null}

      {food && f.dinnerRemindOn ? (
        <Notice icon={I.phone}>
          {here?.kind === "on"
            ? "Log dinner? arrives like water reminders: from Kaya’s server to the installed app."
            : "Log dinner? will arrive like water reminders, from Kaya’s server to the installed app. Until this device is set up for them, it shows only while Kaya is open."}
        </Notice>
      ) : null}

      {/* The only nudge a web app can guarantee is one the phone's calendar owns — which is also
          why Log dinner? is not in it: a calendar cannot know whether dinner is logged. */}
      <RowGroup>
        <NavRow
          kind="action"
          icon={I.calendar}
          title="Add to calendar"
          subtitle={
            `The two training times as daily events (.ics) — ${f.remindMorning} and ${f.remindEvening}.` +
            (food ? " A calendar can’t tell whether dinner is logged, so Log dinner? isn’t in it." : "")
          }
          action={
            <Button variant="ghost" icon={I.dl} onClick={exportCalendar}>
              Add
            </Button>
          }
        />
      </RowGroup>

      {askingAt ? (
        <DinnerTimeSheet
          at={f.dinnerRemindAt}
          onClose={() => setAskingAt(false)}
          onDone={(v) => {
            set("dinnerRemindAt", v);
            setAskingAt(false);
          }}
        />
      ) : null}
    </SettingsPage>
  );
}

/** One field, so a Sheet (invariant 9). It fills the page's form; the page's Save keeps it. Also the
 *  family view's, for a linked person's reminder (ADR-046). */
export function DinnerTimeSheet({
  at: at0,
  onDone,
  onClose,
}: {
  at: string;
  onDone: (at: string) => void;
  onClose: () => void;
}) {
  const [at, setAt] = useState(at0);
  const ok = /^([01]\d|2[0-3]):[0-5]\d$/.test(at);
  return (
    <Sheet
      title="Log dinner? asks at"
      sub="Only if no dinner is logged by then"
      onClose={onClose}
      footer={
        <ActionBar
          inSheet
          secondary={{ label: "Cancel", onClick: onClose }}
          primary={{ label: "Done", disabled: !ok, reason: "Enter a time", onClick: () => onDone(at) }}
        />
      }
    >
      <Field label="Time" type="time" value={at} onChange={setAt} />
    </Sheet>
  );
}
